/**
 * Checklist Item Activities Hook
 *
 * Fetches activity events for a single checklist item and adapts them
 * to the ActivityEvent shape consumed by the ActivityFeed UI component.
 */

import { useQuery } from "convex/react";
import { api } from "convex/_generated/api";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { ChecklistItemId } from "@/packages/types";
import type { ActivityEvent } from "@/components/ui/ActivityFeed/types";
import { createActivityTransformer } from "./activityHelpers";
import {
  createChecklistActivityConfig,
  type ChecklistActivityTranslations,
  type ChecklistActivityType,
} from "./activityConfigs/checklist-config";
import type { RawActivity } from "./activityConfigs/types";

interface UseChecklistItemActivitiesReturn {
  activities: ActivityEvent[];
  isLoading: boolean;
}

export function useChecklistItemActivities(
  checklistItemId: ChecklistItemId | null,
): UseChecklistItemActivitiesReturn {
  const { t } = useTranslation("audits");

  const data = useQuery(
    api.checklist_queries.getChecklistItemActivities,
    checklistItemId ? { checklistItemId } : "skip",
  );

  const isLoading = data === undefined;

  const translations = useMemo<ChecklistActivityTranslations>(
    () => ({
      item: t("detail.activity.checklist.item"),
      unknownError: t("detail.activity.checklist.unknownError"),
      system: t("detail.activity.checklist.system"),
      question: t("detail.activity.checklist.question"),
      itemUpdated: t("detail.activity.checklist.itemUpdated"),
      markedIncomplete: (description, section) =>
        t("detail.activity.checklist.markedIncomplete", {
          description,
          section,
        }),
      completed: (description, section) =>
        t("detail.activity.checklist.completed", { description, section }),
      aiSummaryGenerated: (description) =>
        t("detail.activity.checklist.aiSummaryGenerated", { description }),
      docsFound: (count, description) =>
        t("detail.activity.checklist.docsFound", { count, description }),
      summaryFailed: (description, reason) =>
        t("detail.activity.checklist.summaryFailed", { description, reason }),
      answered: (question) =>
        t("detail.activity.checklist.answered", { question }),
      clearedAnswer: (question) =>
        t("detail.activity.checklist.clearedAnswer", { question }),
    }),
    [t],
  );

  const toActivityEvent = useMemo(
    () =>
      createActivityTransformer(createChecklistActivityConfig(translations)),
    [translations],
  );

  const activities = useMemo<ActivityEvent[]>(() => {
    if (!data) return [];
    return (data as RawActivity<ChecklistActivityType>[]).map(toActivityEvent);
  }, [data, toActivityEvent]);

  return { activities, isLoading };
}
